<script setup lang="ts">
/**
 * One list for every setting („Einstellungen", I-2510): the sections in a
 * navigation on the left with a search over all of them on top, the open
 * section on the right — one row per setting (`NxSettingsRow`): its title,
 * what it does, the control, where the value comes from and „Zurücksetzen".
 * Rarely needed rows fold under „Erweitert (n)" at the end of their section.
 * „Nur geänderte" shows the rows the edited layer sets itself; a search shows
 * the hits of every section at once.
 *
 * Presentational: the host translates its declarations into sections, resolves
 * the edited layer's values and writes what the list emits — `change` with the
 * new value, `reset` and `lock` (an administrator's layer). Its `toolbar` slot
 * holds what the host edits („Bearbeiten für"); `compact` lays the list out for
 * a narrow host: the navigation becomes a select.
 *
 * A `custom` section is an editor of the host's own between the rows' sections
 * (the look designer's colours): the `content` slot renders it, its `topics`
 * take part in the search like rows — while searching, the slot gets the
 * topics that match and shows them under the section's title.
 */
import { computed, inject, useId } from 'vue';
import NxBadge from '../Badge/NxBadge.vue';
import NxDisclosure from '../Disclosure/NxDisclosure.vue';
import NxCheckboxField from '../Inputs/NxCheckboxField.vue';
import NxNativeSelect from '../Inputs/NxNativeSelect.vue';
import NxFilterSelectInput from '../Inputs/NxFilterSelectInput.vue';
import NxSearchInput from '../Inputs/NxSearchInput.vue';
import { i18nInjectionKey } from '../internal';
import NxList from '../ListRow/NxList.vue';
import NxListRow from '../ListRow/NxListRow.vue';
import NxSettingsRow from './NxSettingsRow.vue';
import NxSettingsField from './NxSettingsField.vue';
import {
  nxSettingsItemMatches,
  nxSettingsSearchWords,
  nxSettingsTopicHits,
  type NxSettingsControlSlot,
  type NxSettingsListItem,
  type NxSettingsListSection,
} from './settingsList';

const props = withDefaults(
  defineProps<{
    sections: readonly NxSettingsListSection[];
    /** Nothing can be changed (no permission, the layer is read-only). */
    disabled?: boolean;
    /** A narrow host: the navigation becomes a select, controls go below the text. */
    compact?: boolean;
    /** Pick one setting from a searchable dropdown, keeping its standard row. */
    presentation?: 'list' | 'picker';
    /** Caption of the setting picker (default: "Setting"). */
    itemLabel?: string;
    /** Control height: `s` on pages, `xs` in narrow inspectors. */
    height?: 'xs' | 's';
    /** The search field's placeholder (default: „Einstellungen durchsuchen …"). */
    searchPlaceholder?: string;
  }>(),
  {
    disabled: false,
    compact: false,
    presentation: 'list',
    height: 's',
    searchPlaceholder: undefined,
    itemLabel: undefined,
  }
);

/** The open section; without a binding the first one. */
const section = defineModel<string | undefined>('section', { default: undefined });
/** The search text. */
const search = defineModel<string>('search', { default: '' });
/** Show only the rows the edited layer sets itself. */
const changedOnly = defineModel<boolean>('changedOnly', { default: false });
/** The setting shown in the picker presentation; without a binding the first one. */
const item = defineModel<string | undefined>('item', { default: undefined });

const emit = defineEmits<{
  change: [id: string, value: unknown];
  reset: [id: string];
  lock: [id: string, locked: boolean];
}>();

defineSlots<{
  /** Above the rows, next to „Nur geänderte" — what the host edits („Bearbeiten für"). */
  toolbar?: () => unknown;
  /** Between the toolbar and the rows — a note of the host (read-only, an error). */
  notice?: () => unknown;
  /** Below a row's description — a link that goes deeper. */
  'item-after'?: (props: { item: NxSettingsListItem }) => unknown;
  /** The control of a `custom` row, including while searching or under Advanced. */
  'item-control'?: (props: NxSettingsControlSlot) => unknown;
  /** Below the open section's title — a link to a page of its own. */
  'section-after'?: (props: { section: NxSettingsListSection }) => unknown;
  /** After the open section's rows — what the host adds to the section (a design's pages of an app). */
  'section-end'?: (props: { section: NxSettingsListSection }) => unknown;
  /**
   * The content of a `custom` section: `topics` are the ids of its topics that
   * match the search, `null` when nothing is searched (show everything).
   */
  content?: (props: {
    section: NxSettingsListSection;
    topics: readonly string[] | null;
  }) => unknown;
}>();

const i18n = inject(i18nInjectionKey, null);

function text(key: string, params?: Record<string, unknown>): string {
  const id = `common.settings_list.${key}`;
  if (!i18n) return id;
  return params ? i18n.t(id, params) : i18n.t(id);
}

const idPrefix = useId();

const pickerItems = computed(() =>
  props.sections.flatMap((section) =>
    section.items.map((row) => ({ row, label: `${section.label} · ${row.label}` }))
  )
);
const pickerIndex = computed(
  () => new Map(pickerItems.value.map((entry) => [entry.row.id, entry]))
);
const pickerOptions = computed(() => pickerItems.value.map((entry) => entry.row.id));
const pickedItem = computed(
  () => pickerIndex.value.get(item.value ?? '')?.row ?? pickerItems.value[0]?.row
);
const pickerModel = computed<string[] | [null]>({
  get: (): string[] | [null] => (pickedItem.value ? [pickedItem.value.id] : [null]),
  set: (value: string[] | [null]) => {
    if (value[0] && pickerIndex.value.has(value[0])) item.value = value[0];
  },
});
const pickerText = (id: string) => pickerIndex.value.get(id)?.label ?? id;

function controlId(item: NxSettingsListItem): string {
  return `${idPrefix}-${item.id}`.replace(/[^a-zA-Z0-9_-]/g, '-');
}

const words = computed(() => nxSettingsSearchWords(search.value));
const filtering = computed(() => words.value.length > 0 || changedOnly.value);

const activeSection = computed<NxSettingsListSection | undefined>(
  () => props.sections.find((item) => item.id === section.value) ?? props.sections[0]
);

function ownCount(item: NxSettingsListSection): number {
  return item.items.filter((row) => row.origin === 'own').length;
}

function groupLabel(item: NxSettingsListSection, row: NxSettingsListItem): string {
  return item.groups?.find((group) => group.id === row.group)?.label ?? '';
}

interface Hit {
  section: NxSettingsListSection;
  /** The rows that match. */
  items: NxSettingsListItem[];
  /** The topics of a custom section that match. */
  topics: string[];
  count: number;
}

/**
 * While searching or filtering: the hits of every section, in the sections'
 * order — rows, or the topics of a custom section (which holds no values of
 * its own, so „Nur geänderte" leaves it out).
 */
const hits = computed<Hit[]>(() =>
  props.sections
    .map((item): Hit => {
      if (item.custom) {
        const topics = changedOnly.value ? [] : nxSettingsTopicHits(item, words.value);
        return { section: item, items: [], topics, count: topics.length };
      }
      const items = item.items.filter(
        (row) =>
          (!changedOnly.value || row.origin === 'own') &&
          nxSettingsItemMatches(row, words.value, {
            section: item.label,
            group: groupLabel(item, row),
          })
      );
      return { section: item, items, topics: [], count: items.length };
    })
    .filter((entry) => entry.count > 0)
);

const hitCounts = computed(
  () => new Map(hits.value.map((entry) => [entry.section.id, entry.count]))
);

interface Block {
  id: string;
  label: string;
  items: NxSettingsListItem[];
}

/** Rows of a section in blocks: the ungrouped ones first, then one per group, in the groups' order. */
function blocksOf(item: NxSettingsListSection, rows: readonly NxSettingsListItem[]): Block[] {
  const known = new Set((item.groups ?? []).map((group) => group.id));
  const blocks: Block[] = [
    { id: '', label: '', items: rows.filter((row) => !row.group || !known.has(row.group)) },
    ...(item.groups ?? []).map((group) => ({
      id: group.id,
      label: group.label,
      items: rows.filter((row) => row.group === group.id),
    })),
  ];
  return blocks.filter((block) => block.items.length > 0);
}

const activeBlocks = computed(() =>
  activeSection.value
    ? blocksOf(
        activeSection.value,
        activeSection.value.items.filter((row) => !row.advanced)
      )
    : []
);

const advancedBlocks = computed(() =>
  activeSection.value
    ? blocksOf(
        activeSection.value,
        activeSection.value.items.filter((row) => row.advanced)
      )
    : []
);

const advancedCount = computed(() =>
  advancedBlocks.value.reduce((sum, block) => sum + block.items.length, 0)
);

/** „Erweitert" per section: folded on arrival. */
const advancedOpen = defineModel<readonly string[]>('advancedOpen', { default: () => [] });

const isAdvancedOpen = computed(() =>
  activeSection.value ? advancedOpen.value.includes(activeSection.value.id) : false
);

function setAdvancedOpen(open: boolean): void {
  const id = activeSection.value?.id;
  if (!id) return;
  const rest = advancedOpen.value.filter((item) => item !== id);
  advancedOpen.value = open ? [...rest, id] : rest;
}

/** A section picked in the navigation: the search and the filter step aside for it. */
function openSection(id: string): void {
  section.value = id;
  if (filtering.value) {
    search.value = '';
    changedOnly.value = false;
  }
}

/** The number a section shows in the navigation: its hits while filtering, else its own values. */
function countOf(item: NxSettingsListSection): number {
  return filtering.value ? (hitCounts.value.get(item.id) ?? 0) : ownCount(item);
}

function countTitle(item: NxSettingsListSection): string {
  const count = countOf(item);
  return filtering.value ? text('hit_count', { count }) : text('own_count', { count });
}

const sectionOptions = computed(() =>
  props.sections.map((item) => {
    const count = countOf(item);
    return { value: item.id, label: count ? `${item.label} (${count})` : item.label };
  })
);

const sectionModel = computed<string | number | null>({
  get: () => activeSection.value?.id ?? null,
  set: (id) => {
    if (typeof id === 'string') openSection(id);
  },
});

const emptyText = computed(() => {
  if (words.value.length) return text('no_hits', { query: search.value.trim() });
  return text('no_changes');
});

function onChange(item: NxSettingsListItem, value: unknown): void {
  emit('change', item.id, value);
}
</script>

<template>
  <div
    v-if="presentation === 'picker'"
    class="nx-settings-list is-picker"
    data-testid="settings-list"
  >
    <slot name="toolbar" />
    <NxSettingsField :label="itemLabel || text('setting')">
      <NxFilterSelectInput
        v-model="pickerModel"
        :id="`${idPrefix}-picker`"
        :options="pickerOptions"
        :option-text="pickerText"
        :placeholder="searchPlaceholder || text('search_placeholder')"
        :height="height"
        data-testid="settings-list-item-select"
      />
    </NxSettingsField>
    <slot name="notice" />
    <NxSettingsRow
      v-if="pickedItem"
      :key="pickedItem.id"
      class="nx-settings-list-picked-row"
      :item="pickedItem"
      :disabled="disabled"
      :height="height"
      :compact="compact"
      :control-id="controlId(pickedItem)"
      @change="onChange(pickedItem, $event)"
      @reset="emit('reset', pickedItem.id)"
      @lock="emit('lock', pickedItem.id, $event)"
    >
      <template v-if="$slots['item-control']" #control="shown">
        <slot name="item-control" v-bind="shown" />
      </template>
      <template v-if="$slots['item-after']" #after>
        <slot name="item-after" :item="pickedItem" />
      </template>
    </NxSettingsRow>
    <p v-else class="nx-settings-list-empty">{{ text('empty') }}</p>
  </div>
  <div
    v-else
    class="nx-settings-list"
    :class="{ 'is-compact': compact }"
    data-testid="settings-list"
  >
    <aside class="nx-settings-list-nav">
      <div class="nx-settings-list-search" role="search">
        <NxSearchInput
          v-model="search"
          height="s"
          :placeholder="searchPlaceholder || text('search_placeholder')"
          :aria-label="text('search_label')"
          data-testid="settings-list-search"
        />
      </div>
      <NxNativeSelect
        v-if="compact"
        v-model="sectionModel"
        class="nx-settings-list-section-select"
        size="full"
        :aria-label="text('sections')"
        :options="sectionOptions"
        data-testid="settings-list-section-select"
      />
      <nav v-else class="nx-settings-list-sections" :aria-label="text('sections')">
        <NxList>
          <NxListRow
            v-for="item in sections"
            :key="item.id"
            dense
            class="nx-settings-list-section"
            :selected="!filtering && item.id === activeSection?.id"
            :data-section="item.id"
            data-testid="settings-list-section"
            @open="openSection(item.id)"
          >
            <template #title>
              <span
                class="nx-settings-list-section-label"
                :class="{ 'is-miss': filtering && !hitCounts.get(item.id) }"
              >
                {{ item.label }}
              </span>
            </template>
            <template v-if="countOf(item)" #trailing>
              <NxBadge
                class="nx-settings-list-count"
                size="xs"
                :variant="filtering ? 'neutral' : 'default'"
                :title="countTitle(item)"
              >
                {{ countOf(item) }}
              </NxBadge>
            </template>
          </NxListRow>
        </NxList>
      </nav>
    </aside>

    <section class="nx-settings-list-main" :aria-label="activeSection?.label">
      <div class="nx-settings-list-toolbar">
        <div class="nx-settings-list-toolbar-start">
          <slot name="toolbar" />
        </div>
        <NxCheckboxField
          v-model="changedOnly"
          :label="text('changed_only')"
          :title="text('changed_only_hint')"
          data-testid="settings-list-changed-only"
        />
      </div>

      <slot name="notice" />

      <template v-if="filtering">
        <p v-if="!hits.length" class="nx-settings-list-empty" role="status">{{ emptyText }}</p>
        <section
          v-for="entry in hits"
          :key="entry.section.id"
          class="nx-settings-list-block"
          :data-section="entry.section.id"
        >
          <h3 class="nx-settings-list-hit-title">{{ entry.section.label }}</h3>
          <div v-if="entry.section.custom" class="nx-settings-list-custom">
            <slot name="content" :section="entry.section" :topics="entry.topics" />
          </div>
          <template v-else>
            <template v-for="block in blocksOf(entry.section, entry.items)" :key="block.id">
              <h4 v-if="block.label" class="nx-settings-list-group">{{ block.label }}</h4>
              <div class="nx-settings-list-rows">
                <NxSettingsRow
                  v-for="item in block.items"
                  :key="item.id"
                  :item="item"
                  :disabled="disabled"
                  :height="height"
                  :compact="compact"
                  :control-id="controlId(item)"
                  @change="onChange(item, $event)"
                  @reset="emit('reset', item.id)"
                  @lock="emit('lock', item.id, $event)"
                >
                  <template v-if="$slots['item-control']" #control="shown">
                    <slot name="item-control" v-bind="shown" />
                  </template>
                  <template v-if="$slots['item-after']" #after>
                    <slot name="item-after" :item="item" />
                  </template>
                </NxSettingsRow>
              </div>
            </template>
          </template>
        </section>
      </template>

      <template v-else-if="activeSection?.custom">
        <header class="nx-settings-list-head">
          <h2 class="nx-settings-list-title">{{ activeSection.label }}</h2>
          <p v-if="activeSection.description" class="nx-settings-list-description">
            {{ activeSection.description }}
          </p>
          <slot name="section-after" :section="activeSection" />
        </header>
        <div class="nx-settings-list-custom" :data-section="activeSection.id">
          <slot name="content" :section="activeSection" :topics="null" />
        </div>
      </template>

      <template v-else-if="activeSection">
        <header class="nx-settings-list-head">
          <h2 class="nx-settings-list-title">{{ activeSection.label }}</h2>
          <p v-if="activeSection.description" class="nx-settings-list-description">
            {{ activeSection.description }}
          </p>
          <slot name="section-after" :section="activeSection" />
        </header>
        <template v-for="block in activeBlocks" :key="block.id">
          <h3 v-if="block.label" class="nx-settings-list-group">{{ block.label }}</h3>
          <div class="nx-settings-list-rows">
            <NxSettingsRow
              v-for="item in block.items"
              :key="item.id"
              :item="item"
              :disabled="disabled"
              :height="height"
              :compact="compact"
              :control-id="controlId(item)"
              @change="onChange(item, $event)"
              @reset="emit('reset', item.id)"
              @lock="emit('lock', item.id, $event)"
            >
              <template v-if="$slots['item-control']" #control="shown">
                <slot name="item-control" v-bind="shown" />
              </template>
              <template v-if="$slots['item-after']" #after>
                <slot name="item-after" :item="item" />
              </template>
            </NxSettingsRow>
          </div>
        </template>
        <NxDisclosure
          v-if="advancedCount"
          class="nx-settings-list-advanced"
          :open="isAdvancedOpen"
          :summary="text('advanced', { count: advancedCount })"
          data-testid="settings-list-advanced"
          @update:open="setAdvancedOpen"
        >
          <template v-for="block in advancedBlocks" :key="block.id">
            <h3 v-if="block.label" class="nx-settings-list-group">{{ block.label }}</h3>
            <div class="nx-settings-list-rows">
              <NxSettingsRow
                v-for="item in block.items"
                :key="item.id"
                :item="item"
                :disabled="disabled"
                :height="height"
                :compact="compact"
                :control-id="controlId(item)"
                @change="onChange(item, $event)"
                @reset="emit('reset', item.id)"
                @lock="emit('lock', item.id, $event)"
              >
                <template v-if="$slots['item-control']" #control="shown">
                  <slot name="item-control" v-bind="shown" />
                </template>
                <template v-if="$slots['item-after']" #after>
                  <slot name="item-after" :item="item" />
                </template>
              </NxSettingsRow>
            </div>
          </template>
        </NxDisclosure>
        <slot name="section-end" :section="activeSection" />
      </template>
      <p v-else class="nx-settings-list-empty">{{ text('empty') }}</p>
    </section>
  </div>
</template>

<style scoped>
/* Navigation left (sticky in the scrolling page around it), the open section
   right. The list itself never scrolls: its host does. */
.nx-settings-list {
  display: grid;
  grid-template-columns: calc(var(--nxt-size-unit) * 14) minmax(0, 1fr);
  align-items: start;
  gap: var(--nxt-space-lg);
  min-width: 0;
}

/* A bounded editor for one setting, even when the catalog has hundreds. */
.nx-settings-list.is-picker {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--nxt-space-sm);
}

.nx-settings-list-picked-row {
  margin-top: var(--nxt-space-xs);
  border-top: var(--nxt-hairline) solid var(--nxt-border-subtle);
}

.nx-settings-list-nav {
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  min-width: 0;
  padding-top: var(--nxt-space-2xs);
}

/* The sections: the kit's list rows, the open one selected. */
.nx-settings-list-section-label {
  font-weight: var(--nxt-font-weight-regular);
}

/* While searching: a section without a hit recedes. */
.nx-settings-list-section-label.is-miss {
  color: var(--nxt-fg-subtle);
}

.nx-settings-list-main {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  min-width: 0;
}

.nx-settings-list-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-xs) var(--nxt-space-md);
  min-height: var(--nxt-control-size-lg);
  padding-bottom: var(--nxt-space-xs);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
}

.nx-settings-list-toolbar-start {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
}

.nx-settings-list-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--nxt-space-2xs);
}

.nx-settings-list-title {
  margin: 0;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-lg);
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-settings-list-description {
  max-width: 72ch;
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}

.nx-settings-list-hit-title {
  margin: var(--nxt-space-xs) 0 0;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-settings-list-group {
  margin: var(--nxt-space-sm) 0 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* Rows divided by hairlines, no boxes. */
.nx-settings-list-rows {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.nx-settings-list-rows > :deep(.nx-settings-row + .nx-settings-row) {
  border-top: var(--nxt-hairline) solid var(--nxt-border-subtle);
}

.nx-settings-list-block {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* A custom section: the host's editor, as wide as the rows. */
.nx-settings-list-custom {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-top: var(--nxt-space-2xs);
}

.nx-settings-list-advanced {
  padding-top: var(--nxt-space-xs);
  border-top: var(--nxt-hairline) solid var(--nxt-border-subtle);
}

.nx-settings-list-empty {
  margin: 0;
  padding: var(--nxt-space-md) 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}

/* A narrow host: search and section select on top, the rows below. */
.nx-settings-list.is-compact {
  grid-template-columns: minmax(0, 1fr);
  gap: var(--nxt-space-xs);
}

.is-compact .nx-settings-list-nav {
  z-index: var(--nxt-z-raised);
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-2xs) 0 var(--nxt-space-xs);
  background: var(--nxt-surface-bg);
}

.is-compact .nx-settings-list-title {
  font-size: var(--nxt-font-size-md);
}

@media (max-width: 47.5em) {
  .nx-settings-list:not(.is-compact) {
    grid-template-columns: minmax(0, 1fr);
  }

  .nx-settings-list:not(.is-compact) .nx-settings-list-nav {
    position: static;
  }

  .nx-settings-list:not(.is-compact) .nx-settings-list-sections :deep(.nx-list) {
    flex-flow: row wrap;
  }
}
</style>
